// The Meter: a prompt's tokens and cost as a range, before it is sent, at the rates the reader
// types in. It says plainly that only the provider knows the real number (Charter principle 2).
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { dollars, estimate, type Range } from "../../tools/meter";
import { useTitle } from "../../ui/hooks";
import { PageHero } from "../../ui/Section";
import { Big, NumberField } from "./Field";

const LENGTHS: { label: string; range: Range }[] = [
  { label: "A short answer", range: { low: 150, high: 600 } },
  { label: "A page", range: { low: 600, high: 1500 } },
  { label: "A long report", range: { low: 2000, high: 6000 } },
  { label: "An agent task", range: { low: 15000, high: 120000 } },
];

const THINKING: { label: string; range: Range }[] = [
  { label: "Off", range: { low: 0, high: 0 } },
  { label: "Some", range: { low: 0.5, high: 3 } },
  { label: "Deep", range: { low: 2, high: 10 } },
];

export function Meter() {
  useTitle("The Meter");
  const [prompt, setPrompt] = useState("");
  const [context, setContext] = useState(0);
  const [length, setLength] = useState(1);
  const [thinking, setThinking] = useState(0);
  const [inputRate, setInputRate] = useState(3);
  const [outputRate, setOutputRate] = useState(15);
  const e = useMemo(
    () => estimate({ prompt, context, output: LENGTHS[length]!.range, thinking: THINKING[thinking]!.range, inputRate, outputRate }),
    [prompt, context, length, thinking, inputRate, outputRate],
  );
  const fmt = (r: Range) => (r.low === r.high ? r.low.toLocaleString("en-GB") : `${r.low.toLocaleString("en-GB")} – ${r.high.toLocaleString("en-GB")}`);

  return (
    <>
      <PageHero kicker="§ Free tools · The Meter" title={<>See the meter before the run.</>}>
        Paste a prompt, say roughly what you expect back, and type in your provider's published rates. You get a range, not a number, because a range is the most honest answer
        anyone outside the provider can give.
      </PageHero>
      <section className="wrap grid gap-8 py-12 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-6">
          <div>
            <label htmlFor="meter-prompt" className="block text-[14px] font-semibold">
              Your prompt
            </label>
            <textarea id="meter-prompt" value={prompt} onChange={(ev) => setPrompt(ev.target.value)} rows={9} placeholder="Paste the prompt you are about to send…" className="field mt-1.5 font-mono text-[13.5px]" />
            <p className="mt-1 text-[12.5px] text-ink3">{prompt.length.toLocaleString("en-GB")} characters. Nothing you type leaves this page.</p>
          </div>
          <NumberField label="Conversation so far" hint="A chat re-sends everything before this message. Leave 0 for a new chat." value={context} onChange={setContext} suffix="tokens" step={500} />
          <fieldset>
            <legend className="text-[14px] font-semibold">What you expect back</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {LENGTHS.map((l, i) => (
                <button key={l.label} type="button" aria-pressed={length === i} onClick={() => setLength(i)} className={`chip !px-3.5 !py-1.5 !text-[12px] ${length === i ? "!border-ink !bg-ink !text-paper" : ""}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-[14px] font-semibold">Hidden reasoning (“thinking”)</legend>
            <p className="text-[12.5px] text-ink3">Reasoning models bill their thinking as output, and you rarely see it.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {THINKING.map((t, i) => (
                <button key={t.label} type="button" aria-pressed={thinking === i} onClick={() => setThinking(i)} className={`chip !px-3.5 !py-1.5 !text-[12px] ${thinking === i ? "!border-ink !bg-ink !text-paper" : ""}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField label="Input rate" hint="Your provider's published price" value={inputRate} onChange={setInputRate} step={0.05} suffix="$ / M" />
            <NumberField label="Output rate" hint="Usually several times the input rate" value={outputRate} onChange={setOutputRate} step={0.05} suffix="$ / M" />
          </div>
          <p className="text-[12.5px] text-ink3">The rates start as an example. Check your provider's pricing page and type in theirs.</p>
        </div>
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="card p-6 sm:p-7" aria-live="polite">
            <Big label="Estimated cost" value={`${dollars(e.cost.low)} – ${dollars(e.cost.high)}`} tone="text-signal" />
            <dl className="mt-6 space-y-3 border-t border-line pt-5 text-[14.5px]">
              <div className="flex justify-between gap-4">
                <dt className="text-ink2">Tokens in</dt>
                <dd className="font-mono">{fmt(e.inputTokens)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-ink2">Tokens out (with thinking)</dt>
                <dd className="font-mono">{fmt(e.outputTokens)}</dd>
              </div>
            </dl>
            <p className="mt-6 rounded-xl bg-paper2 p-4 text-[13.5px] leading-relaxed text-ink2">
              Only the provider knows the real number: their tokenizer, their hidden reasoning and their caching decide it. That is why the Charter asks them to{" "}
              <Link to="/charter" className="link">
                show the meter before the run
              </Link>
              .
            </p>
          </div>
          <p className="mt-4 text-[13.5px] text-ink2">
            Why this matters: <Link to="/blog/marsellus-wallaces-briefcase" className="link">Marsellus Wallace's Briefcase</Link>.
          </p>
        </aside>
      </section>
    </>
  );
}
